import { AfterViewChecked, AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { URLS } from '../../../data/navigation/navigation.data';
import { THEMES } from '../../../data/product/product.data';
import { FlatPlatform } from '../../../model/product/flat-platform.model';
import { Product } from '../../../model/product/product.model';
import { ProductHero } from '../../../page/landing-layout/tool-pages/product-hero.data';
import { ProductShots, Shot } from '../../../page/landing-layout/tool-pages/product-shots';

export interface ShowcaseTheme {
  product: Product;
  category: string;
}

/**
 * The home's themes box (owner 2026-10-10, a trial on the home-themes-box branch): ONE panel in
 * the look of the product showcases (it wears their stylesheet), showing one theme at a time.
 * Left, the theme's identity (icon, name, tags, its first sentence, stats, its page); right, the
 * theme on the chosen platform with the platform chips and the platform's store button; under both,
 * every theme as an icon, Nature in one row and Cities in the other. The panel wears the shown
 * theme's colours and, faintly, its wallpaper.
 *
 * Presentational: the home owns the state (which theme and platform, the rotation, the store and
 * cart logic it already had for the banner) and feeds it in; the box only reports picks.
 */
@Component( {
  selector: 'app-theme-showcase',
  templateUrl: './theme-showcase.component.html',
  styleUrls: [
    '../product-showcase/product-showcase.component.scss',
    './theme-showcase.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.Default,
} )
export class ThemeShowcaseComponent extends ProductShots implements AfterViewInit, AfterViewChecked, OnDestroy {

  // ProductShots: no stage picture of its own; the lightbox walks the shown theme's platforms
  readonly hero = { src: '' } as ProductHero;
  shots: Shot[] = [];

  /** Every theme, in any order: the picker groups them by category, newest first. */
  @Input() set themes( list: ShowcaseTheme[] | null | undefined ) {
    const groups = new Map<string, ShowcaseTheme[]>();
    ( list || [] ).forEach( t => {
      const key = t.category || 'Themes';
      groups.set( key, [ ...( groups.get( key ) || [] ), t ] );
    } );
    // one row per theme line: the bundle (Collections) on top, then Nature, then Cities (owner
    // 2026-10-10), any other line after them in THEMES order, whatever order the list came in (the
    // home shuffles it)
    const lines = [ 'Collections', ...THEMES.map( line => line.name ).filter( n => n !== 'Collections' ) ];
    const rank = ( name: string ) => ( lines.indexOf( name ) + 1 ) || lines.length + 1;
    this.groups = [ ...groups.entries() ]
      .sort( ( [ a ], [ b ] ) => rank( a ) - rank( b ) )
      .map( ( [ name, items ] ) => ( {
        name,
        label: name === 'Collections' ? 'Bundle' : name,
        // light themes first, then the dark ones (owner 2026-10-10), the newest first in each
        items: [ ...items ].sort( ( x, y ) =>
          ThemeShowcaseComponent.modeRank( x ) - ThemeShowcaseComponent.modeRank( y )
          || ( y.product.releaseDate || '' ).localeCompare( x.product.releaseDate || '' ) ),
      } ) );
  }

  /** 0 for a light theme, 1 for a dark one, 2 for anything else (the bundle). */
  private static modeRank( t: ShowcaseTheme ): number {
    const mode = ( t.product.appearance || '' ).toLowerCase();
    return mode === 'light' ? 0 : mode === 'dark' ? 1 : 2;
  }

  @Input() active: ShowcaseTheme | null = null;
  @Input() platforms: FlatPlatform[] = [];
  @Input() platformIndex = 0;
  /** The screenshot shown (the home commits it once it has loaded, so the swap never flashes). */
  @Input() screenshot: { src: string } | null = null;
  @Input() ctaText = '';
  @Input() ctaLabels: readonly string[] = [];
  @Input() planned = false;
  /** The price of a listing sold on the site (its Add to cart), or nothing: a listing on another
   *  store says nothing here (owner 2026-10-10). */
  @Input() priceLabel = '';
  @Input() downloads = '';
  @Input() statsLoaded = false;
  @Input() rating: number | undefined;
  /** Whether the site is in dark mode: a theme of the other mode keeps the site's surface, tinted. */
  @Input() siteDark = false;

  @Output() themePick = new EventEmitter<string>();
  @Output() platformPick = new EventEmitter<number>();
  @Output() cta = new EventEmitter<void>();
  /** True while the pointer or the keyboard focus is in the box, or the box is off screen: the
   *  home holds its rotation then (nothing turns under the visitor's hand or out of sight). */
  @Output() hold = new EventEmitter<boolean>();

  private pointerIn = false;
  private focusIn = false;
  private onScreen = false;
  private observer?: IntersectionObserver;

  constructor( private host: ElementRef<HTMLElement>, private zone: NgZone ) {
    super();
  }

  /** The screenshot clicked: enlarged in the site's lightbox, the arrows walking this theme's
   *  platforms (owner 2026-10-10: the picture opens, the page is the Discover button's). */
  enlarge(): void {
    const p = this.product;
    if ( !p ) {
      return;
    }
    // one entry per picture: VS Code and the editors served through Open VSX share one, named after
    // the platform on show when it is one of them, else after the first that has it
    const shown = this.platform;
    const bySrc = new Map<string, FlatPlatform>();
    this.platforms.filter( pl => pl.screenshot?.src ).forEach( pl => {
      const src = pl.screenshot!.src;
      if ( !bySrc.has( src ) || pl === shown ) {
        bySrc.set( src, pl );
      }
    } );
    const title = this.title;
    this.shots = [ ...bySrc.entries() ].map( ( [ src, pl ] ) =>
      ( { name: `${ title } on ${ pl.name }`, src, large: src, alt: `${ title } on ${ pl.name }` } ) );
    const current = this.shots.find( s => s.src === this.screenshot?.src ) || this.shots[ 0 ];
    if ( current ) {
      this.openLightbox( current );
      this.emitHold();
    }
  }

  override closeLightbox(): void {
    super.closeLightbox();
    this.emitHold();
  }

  ngAfterViewInit(): void {
    if ( typeof IntersectionObserver === 'undefined' ) {
      this.onScreen = true;
      return;
    }
    this.zone.runOutsideAngular( () => {
      this.observer = new IntersectionObserver( entries => {
        const visible = entries.some( e => e.isIntersecting );
        if ( visible !== this.onScreen ) {
          this.zone.run( () => {
            this.onScreen = visible;
            this.emitHold();
          } );
        }
      }, { threshold: 0.25 } );
      this.observer.observe( this.host.nativeElement );
    } );
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  pointer( inside: boolean ): void {
    this.pointerIn = inside;
    this.emitHold();
  }

  focus( inside: boolean, event?: FocusEvent ): void {
    // focus moving between two controls inside the box is not leaving it
    if ( !inside && event?.relatedTarget && this.host.nativeElement.contains( event.relatedTarget as Node ) ) {
      return;
    }
    this.focusIn = inside;
    this.emitHold();
  }

  private emitHold(): void {
    this.hold.emit( this.pointerIn || this.focusIn || !this.onScreen || !!this.lightbox );
  }

  groups: { name: string; label: string; items: ShowcaseTheme[] }[] = [];

  // ---- the theme row ----
  @ViewChild( 'tray' ) private trayRef?: ElementRef<HTMLElement>;
  private lastCentredKey = '';

  trackGroup( _: number, g: { name: string } ): string {
    return g.name;
  }

  /** Where the row scrolls (phones), the shown theme's icon is brought into view, sideways only (the
   *  page never scrolls for it). */
  ngAfterViewChecked(): void {
    const key = this.product?.key || '';
    const tray = this.trayRef?.nativeElement;
    if ( !tray || !key || key === this.lastCentredKey ) {
      return;
    }
    const icon = tray.querySelector<HTMLElement>( `[data-key="${ key }"]` );
    if ( !icon ) {
      return;
    }
    // the first time at once (the page has just opened), afterwards smoothly
    const behavior: ScrollBehavior = this.lastCentredKey ? 'smooth' : 'auto';
    this.lastCentredKey = key;
    if ( tray.scrollWidth > tray.clientWidth ) {
      const left = icon.getBoundingClientRect().left - tray.getBoundingClientRect().left + tray.scrollLeft;
      tray.scrollTo( { left: Math.max( 0, left - ( tray.clientWidth - icon.offsetWidth ) / 2 ), behavior } );
    }
  }

  readonly themeUrl = URLS.theme;

  get product(): Product | null {
    return this.active?.product ?? null;
  }

  /** "Dublin Theme"; the bundle is themes, plural ("All In One Themes", as everywhere on the site). */
  titleOf( p: Product | null | undefined ): string {
    return p ? `${ p.name } ${ p.key === 'all-in-one' ? 'Themes' : 'Theme' }` : '';
  }

  get title(): string {
    return this.titleOf( this.product );
  }

  get platform(): FlatPlatform | null {
    return this.platforms[ this.platformIndex ] ?? null;
  }

  /** The theme's story: whole sentences from its description, as many as fill the four lines
   *  (owner 2026-10-10: Vienna's one short sentence left the box half empty). */
  get story(): string {
    const sentences = ( this.product?.description || '' ).match( /[^.!?]+[.!?]+(\s|$)/g ) || [];
    let story = '';
    for ( const s of sentences ) {
      const next = ( story + s ).trim();
      if ( story && next.length > 230 ) {
        break;
      }
      story = next + ' ';
      if ( story.length >= 150 ) {
        break;
      }
    }
    return story.trim() || this.product?.description || '';
  }

  /** One line under the right side's headline about the platform shown, as the app boxes carry
   *  one under theirs (owner 2026-10-10). */
  get platformLine(): string {
    const pl = this.platform;
    if ( !pl ) {
      return '';
    }
    if ( pl.parentName ) {
      return `Served through ${ pl.parentName }, for ${ pl.name } and every editor built on VS Code`;
    }
    return ThemeShowcaseComponent.PLATFORM_LINES[ pl.name ] || `${ pl.name } in the theme's colors`;
  }

  private static readonly PLATFORM_LINES: Record<string, string> = {
    'JetBrains': 'The interface and the editor of IntelliJ IDEA, WebStorm, PyCharm and every JetBrains IDE',
    'VS Code': 'The editor, the sidebar and the panels of Visual Studio Code',
    'Chrome': 'Your tabs, toolbar and new tab page in the theme\'s colors',
    'Firefox': 'Your Firefox tabs, toolbar and address bar in the theme\'s colors',
    'Sublime Text': 'A color scheme and a matching interface for Sublime Text',
    'MacOS Terminal': 'A profile for the Terminal app that comes with your Mac',
    'iTerm2': 'A color preset for iTerm2 with the theme\'s palette',
    'Ghostty': 'Your Ghostty terminal in the theme\'s palette',
    'Claude Code': 'Claude Code in the theme\'s colors, the code in your terminal\'s',
    'OpenCode': 'OpenCode in the theme\'s colors, from panels to code',
    'Codex': 'Codex with code, diffs and Markdown in the theme\'s colors',
    'Slack': 'Your Slack sidebar in the theme\'s colors',
    'Line': 'A LINE theme for your chats, menus and passcode screen',
    'Rhino': 'The Rhino 8 interface on your Mac in the theme\'s colors',
  };

  /** A site listing that costs nothing ("Free", "$0"): its note says open source, not a purchase. */
  get isFree(): boolean {
    const label = ( this.priceLabel || '' ).trim().toLowerCase();
    return label === 'free' || /^\D?0+(\.0+)?$/.test( label );
  }

  get tokens(): any {
    return this.product?.designTokens || {};
  }

  /**
   * The box in the theme's own colours (owner 2026-10-10): when the theme's mode is the site's, its
   * window background, its foregrounds and its lines; when it is the other mode, the site's surface
   * and text stay (a light theme on the dark site keeps a dark glass), taken toward the theme's
   * colour. The site's own values come in from the host (--tsx-site-*, theme-showcase.scss).
   */
  get colorVars(): Record<string, string> {
    const t = this.tokens;
    const primary = t.primaryColor || 'var(--theme-color)';
    const themeDark = ( this.product?.appearance || '' ).toLowerCase() === 'dark';
    const sameMode = themeDark === this.siteDark && !!t.windowBackground && !!t.primaryForeground;
    const base: Record<string, string> = {
      '--plugin-primary': primary,
      '--plugin-secondary': t.secondaryColor || primary,
    };
    if ( sameMode ) {
      return {
        ...base,
        '--tsx-bg': `color-mix(in srgb, ${ t.windowBackground } 72%, var(--tsx-site-bg))`,
        // the theme's own input background, as is (owner 2026-10-10: the theme cards and the platform
        // logos sit on it)
        '--tsx-chip': t.inputBackground || t.windowBackground,
        '--tsx-fg': t.primaryForeground,
        // the secondary text a third of the way to the primary, so it keeps 4.5:1 on the ground
        '--tsx-fg2': t.secondaryForeground ? `color-mix(in srgb, ${ t.secondaryForeground } 70%, ${ t.primaryForeground })` : t.primaryForeground,
      };
    }
    // the other mode: the site's own surface and text, taken toward the theme's colour: its accent,
    // or its own surface when the accent is not a tone of its ground (owner 2026-10-10: London's red
    // on slate, Etna's red on charcoal; the glass is their surface, the accents stay red)
    const tint = ThemeShowcaseComponent.accentFitsGround( t.primaryColor, t.windowBackground ) ? primary : ( t.surface || t.windowBackground );
    return {
      ...base,
      '--tsx-bg': `color-mix(in srgb, var(--tsx-site-bg) 86%, ${ tint })`,
      '--tsx-chip': `color-mix(in srgb, var(--tsx-site-bg) 90%, ${ tint })`,
      '--tsx-fg': `color-mix(in srgb, var(--tsx-site-fg) 84%, ${ tint })`,
      // the secondary text toward the theme too, then nearly half way to the site's own text, so it
      // keeps 4.5:1 on the tinted ground
      '--tsx-fg2': `color-mix(in srgb, color-mix(in srgb, var(--tsx-site-fg2) 80%, ${ tint }) 55%, var(--tsx-site-fg))`,
    };
  }

  /** Whether the accent is a tone of the ground: a hue within 60 degrees of a ground that has a hue
   *  at all (saturation 5% or more). True when either colour is missing, so the accent is used. */
  private static accentFitsGround( accent?: string, ground?: string ): boolean {
    const a = ThemeShowcaseComponent.hueSat( accent );
    const g = ThemeShowcaseComponent.hueSat( ground );
    if ( !a || !g ) {
      return true;
    }
    const d = Math.abs( a.h - g.h );
    return g.s >= 0.05 && Math.min( d, 360 - d ) <= 60;
  }

  /** Hue (degrees) and HSL saturation (0..1) of a #rrggbb colour, or null. */
  private static hueSat( hex?: string ): { h: number; s: number } | null {
    const m = /^#([0-9a-f]{6})$/i.exec( ( hex || '' ).trim() );
    if ( !m ) {
      return null;
    }
    const n = parseInt( m[ 1 ], 16 );
    const [ r, g, b ] = [ ( n >> 16 ) & 255, ( n >> 8 ) & 255, n & 255 ].map( v => v / 255 );
    const max = Math.max( r, g, b ), min = Math.min( r, g, b ), d = max - min, l = ( max + min ) / 2;
    if ( !d ) {
      return { h: 0, s: 0 };
    }
    const s = d / ( 1 - Math.abs( 2 * l - 1 ) );
    const h = max === r ? ( ( g - b ) / d + 6 ) % 6 : max === g ? ( b - r ) / d + 2 : ( r - g ) / d + 4;
    return { h: h * 60, s };
  }

  get wallpaper(): string | null {
    const src = this.product?.backgroundImages?.[ 0 ]?.src;
    return src ? `url(${ src })` : null;
  }

  trackTheme( _: number, t: ShowcaseTheme ): string {
    return t.product.key;
  }

  trackPlatform( _: number, p: FlatPlatform ): string {
    return p.name;
  }
}
